import { useState } from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  AutocompleteInput,
  useTranslate,
  ReferenceInput,
  SelectArrayInput,
  BooleanInput,
  Labeled,
  FormDataConsumer
} from "react-admin";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "@apollo/client";
import * as yup from "yup";
import * as User from "providers/user";
import "yup-phone";

import { usePhotoUploadSubmitEdit } from "hooks/usePhotoUploadSubmitEdit";

import GooglePlacesAutocomplete from "./GooglePlacesAutocomplete";
import PhoneInput from "./PhoneInput";
import CustomDaysTimePicker from "./CustomDaysTimePicker";
import CustomHeaderActions from "./CustomHeaderActions";
import ShowAndEditDetails from "./ShowAndEditDetails";
import { LocationDetailsIcon } from "assets/Icons";
import CustomImageInputWrapper from "./CustomImageInputWrapper";
import CustomImageInput from "./CustomImageInput";
import { amenitiesChoices, checkIfValidLocationOpenHours } from "utils/helpers";
import { Box } from "@mui/material";
import { CustomToolbar } from "./CustomToolbar";
import { timezoneChoices } from "../utils/timezone";
import { isPhoneValid } from "utils/isPhoneValid";
import { useNavigate } from "react-router-dom";
import { atUsernameRegex, facebookRegex } from "utils/validation";
import { GET_COMPANY_PAYMENT_METHOD } from "../gql";

export default function LocationEdit() {
  const { permissions, getUser } = User.useUser();
  const [rerenderTimezone, setRerenderTimezone] = useState(true);
  const navigate = useNavigate();

  const { data: companyData } = useQuery(GET_COMPANY_PAYMENT_METHOD, {
    variables: {
      where: { id: permissions.companyId }
    }
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { submit: submitWithPhoto } = usePhotoUploadSubmitEdit({
    redirect: `/dashboard/Location`,
    resource: "Location",
    keys: ["cover_photo_url"],
    setIsLoading: setIsLoading
  });
  const translate = useTranslate();

  const submit = async (data: any) => {
    setIsLoading(true);

    const customOnSuccess = async (customerUser: any) => {
      getUser();
      navigate("/dashboard/Location");
    };

    const payload = {
      ...data,
      nickname: data?.nickname ? data.nickname : "",
      menu: {
        id: data.menu_id
      }
    };

    submitWithPhoto(payload, customOnSuccess);
  };

  return (
    <Edit
      actions={<CustomHeaderActions title={translate("pagenames.location")} />}
    >
      <SimpleForm
        onSubmit={submit}
        resolver={yupResolver(validationSchema)}
        toolbar={<CustomToolbar isLoading={isLoading} />}
      >
        <ShowAndEditDetails
          title={translate("locations.location_details")}
          view="edit"
          icon={<LocationDetailsIcon />}
        />
        <CustomImageInputWrapper label={translate("locations.cover_photo")}>
          <CustomImageInput
            source="cover_photo_url"
            height="420px"
            width="745px"
          />
        </CustomImageInputWrapper>
        <ReferenceInput
          source="menu_id"
          reference="Menu"
          label={translate("locations.menu")}
          fullWidth
          filter={{ company_id: permissions.companyId }}
        >
          <AutocompleteInput label={translate("locations.menu")} fullWidth />
        </ReferenceInput>
        <TextInput
          source="name"
          label={translate("locations.name")}
          fullWidth
        />
        <TextInput
          source="nickname"
          label={translate("locations.nickname")}
          fullWidth
        />
        {companyData?.company?.is_verified && (
          <BooleanInput
            source="visible_on_homepage"
            label={translate("locations.visible_on_homepage")}
            fullWidth
          />
        )}
        <TextInput
          source="description"
          label={translate("locations.description")}
          fullWidth
          multiline
        />
        <SelectArrayInput
          label={translate("locations.amenities")}
          fullWidth
          source="amenities_enums"
          choices={amenitiesChoices}
          format={(v) => {
            return v?.map(
              (item: string) =>
                amenitiesChoices?.find((curr) => curr.id === item)?.id
            );
          }}
        />
        <TextInput
          source="facebook_url"
          multiline
          fullWidth
          label="Facebook"
          helperText="https://facebook.com/pointment"
        />
        <TextInput
          source="instagram_url"
          multiline
          fullWidth
          label="Instagram"
          helperText="@pointment"
        />
        <TextInput
          source="twitter_url"
          multiline
          fullWidth
          label="Twitter"
          helperText="@pointment"
        />
        <TextInput
          source="website_url"
          multiline
          fullWidth
          label="Website"
          helperText="https://pointmentapp.com"
        />
        <NumberInput
          source="multi_person"
          label={translate("locations.multi_person")}
          fullWidth
          min={0}
          max={3}
          step={1}
          defaultValue={0}
        />
        <PhoneInput source="phone_number" fullWidth />
        <BooleanInput
          source="send_sms"
          label={translate("locations.send_sms")}
          fullWidth
        />
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData.send_sms && (
              <Labeled
                label={translate("locations.sms_phone_number")}
                fullWidth
              >
                <PhoneInput source="sms_phone_number" fullWidth {...rest} />
              </Labeled>
            )
          }
        </FormDataConsumer>
        <Box
          sx={{
            width: "100%",
            "& .MuiFilledInput-root": {
              borderRadius: "8px",
              border: "1px solid rgba(48, 47, 46, 0.06)",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              paddingBottom: "5px !important",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.06)"
              },
              "& fieldset": {
                border: "solid 1px transparent"
              },
              "&:hover fieldset": {
                border: "solid 1px transparent"
              },
              "&.Mui-focused fieldset": {
                border: "solid 1px #b8722a"
              }
            },
            "& .MuiFilledInput-input": {
              height: "18px",
              fontSize: "16px",
              borderRadius: "8px",
              fontFamily: "Inter",
              border: "none"
            }
          }}
        >
          <GooglePlacesAutocomplete />
        </Box>
        <AutocompleteInput
          source="timezone"
          choices={timezoneChoices}
          optionText={(choice: any) => choice.timezone}
          defaultValue="PST8PDT"
          label={translate("locations.timezone")}
          fullWidth
          onChange={() => setRerenderTimezone((prev) => !prev)}
        />
        <h5>Hours</h5>
        <CustomDaysTimePicker
          key={rerenderTimezone?.toString()}
          setRerenderTimezone={setRerenderTimezone}
        />
      </SimpleForm>
    </Edit>
  );
}

const validationSchema = yup
  .object()
  .shape({
    name: yup.string().required("Location name required."),
    nickname: yup.string().nullable(),
    menu_id: yup.string().required("Menu required."),
    timezone: yup.string().required("Timezone required."),
    caption: yup.string().nullable(),
    description: yup.string().nullable(),
    cover_photo_url: yup.object().nullable(),
    visible_on_homepage: yup.boolean().nullable(),
    amenities: yup.array().of(yup.object()).nullable(),
    facebook_url: yup.string().matches(facebookRegex).nullable(),
    instagram_url: yup.string().matches(atUsernameRegex).nullable(),
    twitter_url: yup.string().matches(atUsernameRegex).nullable(),
    website_url: yup.string().url("This field must be a valid URL").nullable(),
    address_display: yup.string().nullable(),
    address_latitude: yup.number().nullable(),
    address_longitude: yup.number().nullable(),
    address: yup.string().nullable().required("Address is required"),
    city: yup.string().nullable().required("City is required"),
    state: yup.string().nullable().required("State is required"),
    zipcode: yup.string().nullable().required("Zipcode is required"),
    country: yup.string().nullable().required("Country is required"),
    multi_person: yup.number().nullable(),
    phone_number: yup
      .string()
      .test("phone", "Invalid phone number", (value) => {
        return isPhoneValid(value);
      }),
    sms_phone_number: yup
      .string()
      .nullable()
      .when("send_sms", {
        is: true,
        then: (schema) =>
          schema
            .required("SMS phone number is required")
            .test("phone", "Invalid phone number", (value) => {
              if (!value) {
                return false;
              }
              return isPhoneValid(value);
            }),
        otherwise: (schema) => schema.notRequired()
      }),
    monday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    monday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    tuesday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    tuesday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    wednesday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    wednesday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    thursday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    thursday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    friday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    friday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    saturday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    saturday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    sunday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    sunday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      )
  })
  .required();
